<template>
  <v-dialog v-model="dialog" max-width="600" persistent>
    <v-card>
      <v-card-title class="subtitle-1">
        <v-icon left small color="success">mdi-check-circle</v-icon>
        API Key Created
      </v-card-title>
      <v-card-text>
        <!-- Warning Alert -->
        <v-alert type="warning" dense text class="mb-4">
          <strong>Important:</strong> This is the only time you will see this key.
          Copy it now and store it securely.
        </v-alert>

        <!-- Key Name -->
        <div class="mb-3">
          <span class="text--secondary">Key Name:</span>
          <strong class="ml-2">{{ keyName }}</strong>
        </div>

        <!-- Raw Key Display -->
        <v-alert outlined color="primary" class="mb-4 api-key-display">
          <div class="d-flex align-center">
            <code class="flex-grow-1 text-wrap api-key-code">{{ rawKey }}</code>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  class="ml-2 flex-shrink-0"
                  v-bind="attrs"
                  v-on="on"
                  @click="copyToClipboard">
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>Copy to clipboard</span>
            </v-tooltip>
          </div>
        </v-alert>

        <!-- Confirmation Checkbox -->
        <v-checkbox
          v-model="confirmed"
          label="I have copied and securely stored this key"
          dense
          hide-details
          class="mt-0" />
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" :disabled="!confirmed" @click="close">
          Done
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ApiKeyCreatedDialog',
  data() {
    return {
      dialog: false,
      rawKey: '',
      keyName: '',
      confirmed: false,
    };
  },
  methods: {
    /**
     * Open the dialog with the created key details
     * @param {string} rawKey - The full API key (only shown once)
     * @param {string} keyName - The display name of the key
     */
    open(rawKey, keyName) {
      this.rawKey = rawKey;
      this.keyName = keyName;
      this.confirmed = false;
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      this.rawKey = '';
      this.keyName = '';
      this.confirmed = false;
    },

    copyToClipboard() {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(this.rawKey)
          .then(() => {
            this.$dialog.notify.success('API key copied to clipboard!', {
              position: 'top-right',
              timeout: 2000,
            });
          })
          .catch(() => {
            this.fallbackCopy();
          });
      } else {
        this.fallbackCopy();
      }
    },

    fallbackCopy() {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = this.rawKey;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (successful) {
          this.$dialog.notify.success('API key copied to clipboard!', {
            position: 'top-right',
            timeout: 2000,
          });
        } else {
          this.$dialog.notify.error('Failed to copy to clipboard');
        }
      } catch (err) {
        this.$dialog.notify.error('Failed to copy to clipboard');
      }
    },
  },
};
</script>

<style scoped>
.api-key-display {
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
  padding: 12px;
  border-radius: 6px;
}

.api-key-code {
  word-break: break-all;
  font-size: 13px;
  background: transparent;
  color: inherit;
}
</style>
